'use client'

import {usePathname, useRouter} from 'next/navigation'
import {useTranslations} from 'next-intl'
import {useTransition} from 'react'
import {Select} from '@/design-system'
import {
  type SearchFilters,
  type SearchMode,
  type SortKey,
  sortsFor,
  toSearchParams
} from '@/lib/search/filters'
import {useSearchTransition} from './SearchPending'

/**
 * The order of the results (SRCH-13).
 *
 * Applied on change rather than on "Show results": choosing an order is not
 * a search being composed, and a student comparing two orders should not
 * have to open the filter sheet twice. A new order starts from the top of
 * the list, so it drops the "Show more" depth.
 */
export function SortSelect({filters, mode}: {filters: SearchFilters; mode: SearchMode}) {
  const t = useTranslations('search')
  const router = useRouter()
  const pathname = usePathname()
  const ownTransition = useTransition()
  const [pending, startTransition] = useSearchTransition() ?? ownTransition

  const options = sortsFor(mode, filters)
  if (options.length === 0) return null
  const current = filters.sort && options.includes(filters.sort) ? filters.sort : options[0]

  return (
    <div className="u-search__sort">
      <Select
        label={t('sortLabel')}
        value={current}
        disabled={pending}
        data-testid="sort"
        options={options.map((key) => ({
          value: key,
          label: t(`sort_${key}`)
        }))}
        onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
          const sort = event.target.value as SortKey
          const query = toSearchParams({...filters, sort}, {mode}).toString()
          startTransition(() => router.push(`${pathname}?${query}`))
        }}
      />
    </div>
  )
}
